import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';

const RoadmapImplementation = () => {
  const [progressWidth, setProgressWidth] = useState('0%');

  // Animate progress bar on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setProgressWidth('35%');
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Animate phase cards on scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'scale(1)';
        }
      });
    }, { threshold: 0.1 });

    const cards = document.querySelectorAll('.phase-card');
    cards.forEach(card => {
      card.style.opacity = '0';
      card.style.transform = 'scale(0.95)';
      card.style.transition = 'all 0.6s ease-out';
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0a0a0a;
            color: #ffffff;
            line-height: 1.6;
            overflow-x: hidden;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .back-link {
            color: #667eea;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: gap 0.3s;
        }
        
        .back-link:hover {
            gap: 1rem;
        }
        
        /* Main Container */
        .container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 100px 2rem 2rem;
        }
        
        /* Header */
        .page-header {
            text-align: center;
            margin-bottom: 4rem;
        }
        
        .page-title {
            font-size: 3.5rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .page-subtitle {
            font-size: 1.3rem;
            color: #888;
        }
        
        /* Progress Overview */
        .progress-overview {
            background: rgba(102, 126, 234, 0.1);
            border: 1px solid rgba(102, 126, 234, 0.3);
            border-radius: 20px;
            padding: 3rem;
            margin-bottom: 4rem;
            text-align: center;
        }
        
        .overall-progress {
            width: 100%;
            height: 40px;
            background: rgba(255, 255, 255, 0.1);
            border-radius: 20px;
            overflow: hidden;
            margin: 2rem 0;
            position: relative;
        }
        
        .progress-bar {
            height: 100%;
            background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
            border-radius: 20px;
            width: 0%;
            transition: width 2s ease-out;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            padding-right: 1rem;
        }
        
        .progress-percentage {
            color: white;
            font-weight: bold;
            text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }
        
        /* Timeline Container */
        .timeline-container {
            position: relative;
            padding: 3rem 0;
            margin: 3rem 0;
        }
        
        /* Timeline Line */
        .timeline-line {
            position: absolute;
            left: 50%;
            top: 0;
            bottom: 0;
            width: 3px;
            background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
            transform: translateX(-50%);
            z-index: 1;
        }
        
        /* Phase Cards */
        .phase-card {
            position: relative;
            width: 48%;
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 20px;
            padding: 2.5rem;
            margin-bottom: 4rem;
            z-index: 2;
            transition: all 0.3s;
        }
        
        .phase-card:nth-child(odd) {
            margin-left: auto;
            margin-right: 0;
        }
        
        .phase-card:nth-child(even) {
            margin-left: 0;
            margin-right: auto;
        }
        
        .phase-card:hover {
            transform: scale(1.02);
            border-color: rgba(102, 126, 234, 0.5);
            background: rgba(255, 255, 255, 0.05);
        }
        
        /* Phase Status Badge */
        .phase-status {
            position: absolute;
            top: -15px;
            right: 20px;
            padding: 0.5rem 1.5rem;
            border-radius: 20px;
            font-size: 0.9rem;
            font-weight: bold;
            text-transform: uppercase;
        }
        
        .status-completed {
            background: linear-gradient(135deg, #67e8a9 0%, #48bb78 100%);
            color: #0a0a0a;
        }
        
        .status-current {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            animation: pulse-glow 2s ease-in-out infinite;
        }
        
        .status-upcoming {
            background: rgba(255, 255, 255, 0.1);
            color: #888;
        }
        
        @keyframes pulse-glow {
            0%, 100% { box-shadow: 0 0 20px rgba(102, 126, 234, 0.5); }
            50% { box-shadow: 0 0 40px rgba(102, 126, 234, 0.8); }
        }
        
        /* Phase Header */
        .phase-header {
            display: flex;
            align-items: center;
            gap: 1.5rem;
            margin-bottom: 1.5rem;
        }
        
        .phase-icon {
            width: 60px;
            height: 60px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.8rem;
            flex-shrink: 0;
        }
        
        .phase-title {
            flex: 1;
        }
        
        .phase-title h2 {
            font-size: 1.8rem;
            margin-bottom: 0.5rem;
        }
        
        .phase-duration {
            color: #888;
            font-size: 1rem;
        }
        
        /* Features List */
        .features-list {
            list-style: none;
            margin: 1.5rem 0;
        }
        
        .features-list li {
            padding: 0.8rem 0;
            display: flex;
            align-items: start;
            gap: 1rem;
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .features-list li:last-child {
            border-bottom: none;
        }
        
        .feature-status {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            font-size: 0.8rem;
        }
        
        .status-done {
            background: #67e8a9;
            color: #0a0a0a;
        }
        
        .status-progress {
            background: #667eea;
            color: white;
            animation: rotate 2s linear infinite;
        }
        
        .status-planned {
            background: rgba(255, 255, 255, 0.1);
            border: 2px solid rgba(255, 255, 255, 0.2);
        }
        
        @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
        
        /* Metrics Grid */
        .metrics-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 1rem;
            margin-top: 1.5rem;
        }
        
        .metric-card {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            padding: 1rem;
            text-align: center;
        }
        
        .metric-value {
            font-size: 1.5rem;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .metric-label {
            font-size: 0.8rem;
            color: #888;
            margin-top: 0.3rem;
        }
        
        /* Infrastructure Section */
        .infra-section {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin: 2rem 0;
        }
        
        .infra-title {
            font-size: 1.3rem;
            font-weight: bold;
            margin-bottom: 1.5rem;
            color: #667eea;
        }
        
        .infra-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 1rem;
        }
        
        .infra-item {
            background: rgba(255, 255, 255, 0.05);
            padding: 1rem;
            border-radius: 8px;
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
        
        .infra-item::before {
            content: '▸';
            color: #667eea;
            font-size: 1.2rem;
        }
        
        /* Stack Table */
        .stack-table {
            width: 100%;
            border-collapse: collapse;
            margin: 2rem 0;
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            overflow: hidden;
        }
        
        .stack-table th,
        .stack-table td {
            padding: 1rem;
            text-align: left;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .stack-table th {
            background: rgba(102, 126, 234, 0.1);
            font-weight: bold;
        }
        
        .stack-table tr:last-child td {
            border-bottom: none;
        }
        
        .stack-table tr:hover {
            background: rgba(255, 255, 255, 0.05);
        }
        
        /* Process Flow */
        .process-section {
            margin: 2rem 0;
        }
        
        .process-steps {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin: 2rem 0;
            position: relative;
        }
        
        .process-step {
            flex: 1;
            text-align: center;
            position: relative;
            z-index: 2;
        }
        
        .process-step::after {
            content: '';
            position: absolute;
            top: 30px;
            left: 60%;
            width: 80%;
            height: 2px;
            background: rgba(102, 126, 234, 0.3);
            z-index: 1;
        }
        
        .process-step:last-child::after {
            display: none;
        }
        
        .step-icon {
            width: 60px;
            height: 60px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 0.5rem;
            font-size: 1.5rem;
            position: relative;
            z-index: 3;
        }
        
        .step-name {
            font-weight: bold;
            margin-bottom: 0.3rem;
        }
        
        .step-desc {
            font-size: 0.8rem;
            color: #888;
        }
        
        /* Benchmark Section */
        .benchmark-container {
            background: rgba(103, 232, 169, 0.1);
            border: 1px solid rgba(103, 232, 169, 0.3);
            border-radius: 16px;
            padding: 2rem;
            margin: 3rem 0;
        }
        
        .benchmark-title {
            text-align: center;
            font-size: 1.5rem;
            margin-bottom: 2rem;
        }
        
        .benchmark-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1.5rem;
        }
        
        .benchmark-item {
            background: rgba(255, 255, 255, 0.05);
            padding: 1.5rem;
            border-radius: 12px;
            text-align: center;
            transition: all 0.3s;
        }
        
        .benchmark-item:hover {
            transform: scale(1.05);
        }
        
        .benchmark-metric {
            font-size: 2rem;
            font-weight: bold;
            color: #67e8a9;
            margin-bottom: 0.5rem;
        }
        
        .benchmark-label {
            color: #888;
            font-size: 0.9rem;
        }
        
        /* CTA Section */
        .cta-section {
            text-align: center;
            margin: 4rem 0;
            padding: 3rem;
            background: rgba(255, 255, 255, 0.03);
            border-radius: 20px;
        }
        
        .cta-button {
            display: inline-block;
            padding: 1rem 3rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            text-decoration: none;
            border-radius: 8px;
            font-weight: 600;
            font-size: 1.1rem;
            transition: all 0.3s;
            margin: 0.5rem;
        }
        
        .cta-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
        }
        
        .cta-button.secondary {
            background: transparent;
            border: 2px solid #667eea;
            color: #667eea;
        }
        
        /* Responsive */
        @media (max-width: 1024px) {
            .timeline-line {
                left: 30px;
            }
            
            .phase-card {
                width: calc(100% - 60px);
                margin-left: 60px !important;
            }
            
            .phase-card:nth-child(even) {
                margin-left: 60px !important;
            }
        }
        
        @media (max-width: 768px) {
            .process-steps {
                flex-direction: column;
                gap: 2rem;
            }
            
            .process-step::after {
                display: none;
            }
            
            .infra-grid {
                grid-template-columns: 1fr;
            }
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="index.html" className="back-link">
            <ChevronLeft size={16} /> Retour à l'accueil
          </a>
          <div>Roadmap d'Implémentation</div>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Roadmap d'Implémentation</h1>
          <p className="page-subtitle">Approche Progressive en 3 Phases pour l'Agent RAG Hybride Banking</p>
        </div>
        
        {/* Progress Overview */}
        <div className="progress-overview">
          <h2>Progression Globale du Projet</h2>
          <div className="overall-progress">
            <div className="progress-bar" style={{ width: progressWidth }}>
              <span className="progress-percentage">35%</span>
            </div>
          </div>
          <p>Phase 1 terminée ✅ | Phase 2 en préparation 🚀 | Phase 3 planifiée 📅</p>
        </div>
        
        {/* Timeline */}
        <div className="timeline-container">
          <div className="timeline-line"></div>
          
          {/* Phase 1 - MVP */}
          <div className="phase-card">
            <div className="phase-status status-completed">Terminé ✅</div>
            <div className="phase-header">
              <div className="phase-icon">🎯</div>
              <div className="phase-title">
                <h2>Phase 1 - MVP</h2>
                <div className="phase-duration">2-4 semaines | Oct-Nov 2024</div>
              </div>
            </div>
            
            <p>RAG Simple pour validation rapide du concept et feedback utilisateurs.</p>
            
            <ul className="features-list">
              <li>
                <span className="feature-status status-done">✓</span>
                <span>RAG Simple : LangChain + FAISS + GPT-4</span>
              </li>
              <li>
                <span className="feature-status status-done">✓</span>
                <span>1 Index vectoriel unifié pour tous les documents</span>
              </li>
              <li>
                <span className="feature-status status-done">✓</span>
                <span>Chunking standard (1000 tokens avec overlap 200)</span>
              </li>
              <li>
                <span className="feature-status status-done">✓</span>
                <span>API REST basique avec FastAPI</span>
              </li>
            </ul>
            
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-value">85%</div>
                <div className="metric-label">Précision</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">&lt; 10k</div>
                <div className="metric-label">Documents</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">&lt; 3s</div>
                <div className="metric-label">Latence</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">$</div>
                <div className="metric-label">Coût</div>
              </div>
            </div>
            
            {/* Infrastructure Phase 1 */}
            <div className="infra-section">
              <h3 className="infra-title">🏗️ Infrastructure Requise</h3>
              <div className="infra-grid">
                <div className="infra-item">Serveur unique (8 vCPU, 32GB RAM)</div>
                <div className="infra-item">Stockage SSD 500GB</div>
                <div className="infra-item">Docker standalone</div>
                <div className="infra-item">Backup manuel</div>
              </div>
            </div>
            
            {/* Stack Phase 1 */}
            <table className="stack-table">
              <thead>
                <tr>
                  <th>Composant</th>
                  <th>Technologie</th>
                  <th>Version</th>
                  <th>Rôle</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Orchestration</td>
                  <td>LangChain</td>
                  <td>0.0.300</td>
                  <td>Workflow RAG simple</td>
                </tr>
                <tr>
                  <td>Vector DB</td>
                  <td>FAISS</td>
                  <td>1.7.4</td>
                  <td>Recherche vectorielle locale</td>
                </tr>
                <tr>
                  <td>LLM</td>
                  <td>OpenAI GPT-4</td>
                  <td>API</td>
                  <td>Génération de réponses</td>
                </tr>
                <tr>
                  <td>API</td>
                  <td>FastAPI</td>
                  <td>0.104</td>
                  <td>Endpoints REST</td>
                </tr>
                <tr>
                  <td>Cache</td>
                  <td>In-memory</td>
                  <td>-</td>
                  <td>Cache basique Python</td>
                </tr>
              </tbody>
            </table>
            
            {/* Process Phase 1 */}
            <div className="process-section">
              <h3 className="infra-title">📋 Processus de Traitement</h3>
              <div className="process-steps">
                <div className="process-step">
                  <div className="step-icon">❓</div>
                  <div className="step-name">Requête</div>
                  <div className="step-desc">API endpoint</div>
                </div>
                <div className="process-step">
                  <div className="step-icon">🔍</div>
                  <div className="step-name">Embedding</div>
                  <div className="step-desc">OpenAI Ada</div>
                </div>
                <div className="process-step">
                  <div className="step-icon">📊</div>
                  <div className="step-name">FAISS</div>
                  <div className="step-desc">Top 5 chunks</div>
                </div>
                <div className="process-step">
                  <div className="step-icon">💬</div>
                  <div className="step-name">GPT-4</div>
                  <div className="step-desc">Génération</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Phase 2 - Optimisation */}
          <div className="phase-card">
            <div className="phase-status status-current">En cours 🚀</div>
            <div className="phase-header">
              <div className="phase-icon">⚡</div>
              <div className="phase-title">
                <h2>Phase 2 - Optimisation</h2>
                <div className="phase-duration">1-2 mois | Déc 2024 - Jan 2025</div>
              </div>
            </div>
            
            <p>Recherche Hybride et optimisations pour production avec la documentation fournie.</p>
            
            <ul className="features-list">
              <li>
                <span className="feature-status status-progress">⟳</span>
                <span>Recherche Hybride (Vector 60% + BM25 25% + Graph 10% + Metadata 5%)</span>
              </li>
              <li>
                <span className="feature-status status-planned"></span>
                <span>2 Index spécialisés (Technique IT/MOE + Compliance BackOffice/AMOA)</span>
              </li>
              <li>
                <span className="feature-status status-planned"></span>
                <span>Cache Redis multi-niveaux (embeddings, résultats, sessions)</span>
              </li>
              <li>
                <span className="feature-status status-planned"></span>
                <span>Re-ranking avec Cross-Encoder</span>
              </li>
              <li>
                <span className="feature-status status-planned"></span>
                <span>Monitoring Prometheus + Grafana</span>
              </li>
            </ul>
            
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-value">92%</div>
                <div className="metric-label">Précision visée</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">&lt; 100k</div>
                <div className="metric-label">Documents</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">&lt; 2s</div>
                <div className="metric-label">Latence P50</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">$$</div>
                <div className="metric-label">Coût</div>
              </div>
            </div>
            
            {/* Infrastructure Phase 2 */}
            <div className="infra-section">
              <h3 className="infra-title">🏗️ Infrastructure Requise</h3>
              <div className="infra-grid">
                <div className="infra-item">3 serveurs (16 vCPU, 64GB RAM chacun)</div>
                <div className="infra-item">Stockage SSD 2TB (distributed)</div>
                <div className="infra-item">Docker Swarm ou K8s léger</div>
                <div className="infra-item">Load Balancer (HAProxy)</div>
                <div className="infra-item">Redis Cluster (3 nodes)</div>
                <div className="infra-item">Elasticsearch cluster</div>
                <div className="infra-item">Neo4j instance</div>
                <div className="infra-item">Backup automatisé (daily)</div>
              </div>
            </div>
            
            {/* Stack Phase 2 */}
            <table className="stack-table">
              <thead>
                <tr>
                  <th>Composant</th>
                  <th>Technologie</th>
                  <th>Version</th>
                  <th>Rôle</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Orchestration</td>
                  <td>LangChain + Router</td>
                  <td>0.1.0</td>
                  <td>Recherche hybride</td>
                </tr>
                <tr>
                  <td>Vector DB</td>
                  <td>Qdrant</td>
                  <td>1.7</td>
                  <td>2 collections spécialisées</td>
                </tr>
                <tr>
                  <td>Keyword Search</td>
                  <td>Elasticsearch</td>
                  <td>8.11</td>
                  <td>BM25 ranking</td>
                </tr>
                <tr>
                  <td>Graph DB</td>
                  <td>Neo4j</td>
                  <td>5.15</td>
                  <td>Relations entre concepts</td>
                </tr>
                <tr>
                  <td>Cache</td>
                  <td>Redis</td>
                  <td>7.2</td>
                  <td>Cache distribué</td>
                </tr>
                <tr>
                  <td>Monitoring</td>
                  <td>Prometheus/Grafana</td>
                  <td>Latest</td>
                  <td>Métriques temps réel</td>
                </tr>
              </tbody>
            </table>
            
            {/* Process Phase 2 */}
            <div className="process-section">
              <h3 className="infra-title">📋 Processus de Traitement Hybride</h3>
              <div className="process-steps">
                <div className="process-step">
                  <div className="step-icon">🧠</div>
                  <div className="step-name">Analyse</div>
                  <div className="step-desc">Intent + Entités</div>
                </div>
                <div className="process-step">
                  <div className="step-icon">🔍</div>
                  <div className="step-name">4 Recherches</div>
                  <div className="step-desc">Parallèles</div>
                </div>
                <div className="process-step">
                  <div className="step-icon">⚡</div>
                  <div className="step-name">Fusion RRF</div>
                  <div className="step-desc">Weighted merge</div>
                </div>
                <div className="process-step">
                  <div className="step-icon">🎯</div>
                  <div className="step-name">Re-ranking</div>
                  <div className="step-desc">Cross-encoder</div>
                </div>
                <div className="process-step">
                  <div className="step-icon">💬</div>
                  <div className="step-name">Génération</div>
                  <div className="step-desc">Context-aware</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Phase 3 - Scale */}
          <div className="phase-card">
            <div className="phase-status status-upcoming">Planifié 📅</div>
            <div className="phase-header">
              <div className="phase-icon">🚀</div>
              <div className="phase-title">
                <h2>Phase 3 - Scale</h2>
                <div className="phase-duration">3-6 mois | Fév - Juillet 2025</div>
              </div>
            </div>
            
            <p>Architecture distribuée et fonctionnalités avancées pour déploiement entreprise.</p>
            
            <ul className="features-list">
              <li>
                <span className="feature-status status-planned"></span>
                <span>Multi-agents spécialisés (si complexité l'exige)</span>
              </li>
              <li>
                <span className="feature-status status-planned"></span>
                <span>Fine-tuning modèle spécifique banking (Mistral/Llama)</span>
              </li>
              <li>
                <span className="feature-status status-planned"></span>
                <span>Analytics ML pour prédiction et amélioration continue</span>
              </li>
              <li>
                <span className="feature-status status-planned"></span>
                <span>Kubernetes production avec auto-scaling</span>
              </li>
              <li>
                <span className="feature-status status-planned"></span>
                <span>Multi-région et disaster recovery</span>
              </li>
            </ul>
            
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-value">95%+</div>
                <div className="metric-label">Précision cible</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">&gt; 1M</div>
                <div className="metric-label">Documents</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">&lt; 1.5s</div>
                <div className="metric-label">Latence P50</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">$$$$</div>
                <div className="metric-label">Coût</div>
              </div>
            </div>
            
            {/* Infrastructure Phase 3 */}
            <div className="infra-section">
              <h3 className="infra-title">🏗️ Infrastructure Requise</h3>
              <div className="infra-grid">
                <div className="infra-item">Kubernetes cluster (20+ nodes)</div>
                <div className="infra-item">GPU nodes pour fine-tuning</div>
                <div className="infra-item">Object storage (S3/MinIO)</div>
                <div className="infra-item">Service mesh (Istio)</div>
                <div className="infra-item">Multi-region deployment</div>
                <div className="infra-item">CDN pour assets</div>
                <div className="infra-item">Disaster recovery site</div>
                <div className="infra-item">24/7 monitoring SOC</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Benchmark Section */}
        <div className="benchmark-container">
          <h2 className="benchmark-title">📊 Benchmarks par Phase</h2>
          <div className="benchmark-grid">
            <div className="benchmark-item">
              <h3>Phase 1 - MVP</h3>
              <div className="benchmark-metric">85%</div>
              <div className="benchmark-label">Précision</div>
              <div className="benchmark-metric">⭐</div>
              <div className="benchmark-label">Complexité</div>
            </div>
            <div className="benchmark-item">
              <h3>Phase 2 - Hybride</h3>
              <div className="benchmark-metric">92%</div>
              <div className="benchmark-label">Précision</div>
              <div className="benchmark-metric">⭐⭐</div>
              <div className="benchmark-label">Complexité</div>
            </div>
            <div className="benchmark-item">
              <h3>Phase 3 - Scale</h3>
              <div className="benchmark-metric">95%+</div>
              <div className="benchmark-label">Précision</div>
              <div className="benchmark-metric">⭐⭐⭐⭐</div>
              <div className="benchmark-label">Complexité</div>
            </div>
          </div>
        </div>
        
        {/* CTA Section */}
        <div className="cta-section">
          <h2>Prêt pour la Phase 2 ?</h2>
          <p style={{ color: '#888', margin: '1rem 0' }}>
            Découvrez le plan d'action détaillé pour implémenter la recherche hybride
          </p>
          <a href="phase2-action-plan.html" className="cta-button">
            Voir le Plan d'Action Phase 2 →
          </a>
          <a href="technical-stack.html" className="cta-button secondary">
            Stack Technique Détaillée
          </a>
        </div>
      </div>
    </>
  );
};

export default RoadmapImplementation;